import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Checkbox, Stack, TextField, Typography } from '@mui/material';
import OfferPortalApi, { OfferRecord, ProposedCondition } from '../../api/OfferPortalApi';
import { CONDITION_TYPE_LABELS } from '../../api/ConditionsApi';

/**
 * The deadlines an accepted offer creates (Lloyd, Aug 19, item 11c), proposed for staff to add to
 * the case. Once added they are ordinary conditions: on the calendar, in the client's portal and in
 * the reminders.
 *
 * The dates can be changed before adding, because they are worked out from what was typed on the
 * offer, and the court's order is the authority. An undated one (a condition ticked on the offer)
 * can be added undated; the client then sees "date to be confirmed".
 *
 * Draws nothing until the offer is accepted, and nothing once every deadline is on the case.
 */
interface Row extends ProposedCondition {
  include: boolean;
  amountUsd: string;
}

function OfferDeadlinesPanel({ offer, onAdded }: { offer: OfferRecord; onAdded?: () => void }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    if (offer.clientResponse !== 'accepted') {
      setRows([]);
      return;
    }
    let cancelled = false;
    OfferPortalApi.proposedConditions(offer.guid)
      .then(({ proposals }) => {
        if (cancelled) return;
        setRows(
          proposals.map((p) => ({
            ...p,
            include: true,
            amountUsd: p.amountCents != null ? (p.amountCents / 100).toFixed(2) : '',
          })),
        );
      })
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : 'Failed to load'));
    return () => {
      cancelled = true;
    };
  }, [offer.guid, offer.clientResponse]);

  const update = (i: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const add = async () => {
    const chosen = rows.filter((r) => r.include);
    if (chosen.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const { added } = await OfferPortalApi.addConditions(
        offer.guid,
        chosen.map((r) => ({
          type: r.type,
          dueAt: r.dueAt || null,
          amountCents: r.amountUsd ? Math.round(Number(r.amountUsd) * 100) : null,
        })),
      );
      setDone(`${added} deadline${added === 1 ? '' : 's'} added to the case.`);
      setRows((prev) => prev.filter((r) => !r.include));
      onAdded?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add the deadlines');
    } finally {
      setSaving(false);
    }
  };

  if (rows.length === 0 && !done && !error) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Deadlines from the accepted offer
      </Typography>
      {rows.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Check each date against the court’s order, then add them. They go on the calendar, in the
          client’s portal and into the reminders.
        </Typography>
      )}
      {done && (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setDone(null)}>
          {done}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack spacing={1.5}>
        {rows.map((r, i) => (
          <Box key={`${r.type}-${i}`} display="flex" gap={1.5} alignItems="flex-start" flexWrap="wrap">
            <Checkbox checked={r.include} onChange={(e) => update(i, { include: e.target.checked })} sx={{ p: 0.5 }} />
            <Box flex="1 1 220px" minWidth={0}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {CONDITION_TYPE_LABELS[r.type]}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.basis}
              </Typography>
            </Box>
            <TextField
              label="Due"
              type="date"
              size="small"
              value={r.dueAt ?? ''}
              onChange={(e) => update(i, { dueAt: e.target.value || null })}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            {r.type === 'PAYMENT' && (
              <TextField
                label="Amount (USD)"
                type="number"
                size="small"
                value={r.amountUsd}
                onChange={(e) => update(i, { amountUsd: e.target.value })}
                sx={{ width: 140 }}
              />
            )}
          </Box>
        ))}
      </Stack>
      {rows.length > 0 && (
        <Button variant="contained" sx={{ mt: 2 }} onClick={add} disabled={saving || !rows.some((r) => r.include)}>
          {saving ? 'Adding…' : 'Add to case'}
        </Button>
      )}
    </Box>
  );
}

export default OfferDeadlinesPanel;
